import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/admin/AuthContext";
import { HOSPITAL } from "@/lib/i18n";
import { LogIn, Loader2, ShieldCheck, Eye, EyeOff } from "lucide-react";

const AdminLogin = () => {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const next = sp.get("next");
      // SIPERKASA operators have no admin panel → always go to SIPERKASA.
      if (u && u.role === "siperkasa") {
        nav("/siperkasa");
      } else if (next && next.startsWith("/") && !next.startsWith("//")) {
        nav(next);
      } else {
        nav("/admin");
      }
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 px-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white rounded-3xl border border-slate-100 shadow-2xl shadow-blue-900/10 p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <img src={HOSPITAL.logo} alt="Logo" className="h-12 w-12 rounded-xl object-cover" />
          <div>
            <div className="font-heading font-bold text-slate-900">Admin Panel</div>
            <div className="text-xs text-slate-500">{HOSPITAL.name}</div>
          </div>
        </div>

        <h1 className="font-heading text-2xl font-bold text-slate-900 mb-1">Masuk</h1>
        <p className="text-sm text-slate-500 mb-6">
          Masukkan kredensial admin Anda untuk mengelola konten website.
        </p>

        {err && (
          <div
            data-testid="admin-login-error"
            className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-100 text-sm text-red-700"
          >
            {err}
          </div>
        )}

        <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-login-email"
          placeholder="admin@rsbhayangkaratarakan.id"
          className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm mb-4"
        />

        <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Password</label>
        <div className="relative mb-6">
          <input
            type={showPassword ? "text" : "password"}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-login-password"
            className="w-full px-4 py-3 pr-12 rounded-xl border border-slate-200 focus:border-blue-800 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            data-testid="admin-login-toggle-password"
            aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
            title={showPassword ? "Sembunyikan password" : "Tampilkan password"}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-slate-400 hover:text-slate-700 transition-colors"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white font-medium px-6 py-3 rounded-full transition shadow-md shadow-blue-900/20"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
          Login
        </button>

        <div className="mt-3 text-center">
          <a
            href="/admin/forgot-password"
            data-testid="admin-login-forgot-link"
            className="text-xs text-blue-800 hover:text-blue-900 hover:underline"
          >
            Lupa password?
          </a>
        </div>

        <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
          <ShieldCheck className="h-3.5 w-3.5" />
          Koneksi aman • JWT authentication
        </div>
      </form>
    </div>
  );
};

export default AdminLogin;
